import { Modal } from '@/components/ui/Modal';
import { ALL_SLIME_TYPES, SLIME_TYPES, type SlimeType } from '@/game/slimes';

interface HowToPlayModalProps {
  open: boolean;
  onClose: () => void;
}

const KEY_BINDINGS: { action: string; keys: string[] }[] = [
  { action: 'Pop left', keys: ['←', 'A', 'Z'] },
  { action: 'Pop right', keys: ['→', 'D', 'X'] },
  { action: 'Pause / resume', keys: ['Esc', 'P', 'Space'] },
  { action: 'Mute', keys: ['M'] },
];

const SLIME_ICON_MODIFIER: Record<SlimeType, string> = {
  [SLIME_TYPES.FIRE]: 'red',
  [SLIME_TYPES.WATER]: 'blue',
  [SLIME_TYPES.PLANT]: 'green',
  [SLIME_TYPES.SMOG]: 'black',
  [SLIME_TYPES.PURPLE]: 'purple',
  [SLIME_TYPES.SOIL]: 'brown',
  [SLIME_TYPES.TIME]: 'time',
};

export function HowToPlayModal({ open, onClose }: HowToPlayModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="How to play">
      <section aria-labelledby="howto-goal" className="howto-section">
        <h3 id="howto-goal">Goal</h3>
        <p>
          Pop the slime at the front of the stack by tapping the side panel that lists its colour.
          Wrong taps break your combo. Catch the gold time slime to add seconds.
        </p>
      </section>

      <section aria-labelledby="howto-keys" className="howto-section">
        <h3 id="howto-keys">Keyboard</h3>
        <table className="howto-keys">
          <tbody>
            {KEY_BINDINGS.map((row) => (
              <tr key={row.action}>
                <th scope="row">{row.action}</th>
                <td>
                  {row.keys.map((k) => (
                    <kbd key={k} className="howto-kbd">
                      {k}
                    </kbd>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-labelledby="howto-touch" className="howto-section">
        <h3 id="howto-touch">Touch &amp; mouse</h3>
        <p>Tap or click the left half of the screen to pop left, the right half to pop right.</p>
      </section>

      <section aria-labelledby="howto-slimes" className="howto-section">
        <h3 id="howto-slimes">Slime types</h3>
        <ul className="howto-slimes" aria-label="Slime palette">
          {ALL_SLIME_TYPES.map((type) => (
            <li key={type} className="howto-slime">
              <span
                aria-hidden="true"
                className={`slime-icon slime-icon--${SLIME_ICON_MODIFIER[type]}`}
              />
              <span>{toLabel(type)}</span>
            </li>
          ))}
          <li className="howto-slime howto-slime--time">
            <span aria-hidden="true" className="slime-icon slime-icon--time" />
            <span>Time slime — adds seconds, always matches</span>
          </li>
        </ul>
      </section>
    </Modal>
  );
}

function toLabel(type: string): string {
  const base = type.replace('-slime', '');
  return base.charAt(0).toUpperCase() + base.slice(1);
}
